import React, { useState, useEffect } from 'react';
import { Row, Col, Container, Form, Button, Alert, Modal, Image, Spinner, FloatingLabel } from 'react-bootstrap';
import { useLogin } from '../../hooks/useLogin';
import { useAuthContext } from '../../hooks/useAuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import ResetPassModal from '../settings/ResetPassModal';
import lucidusLogo from '../../images/chakra_logo.png'
import TraffickStopLogo from '../../images/TraffickStop_logo1.png'

function LoginScreen({ location, history }) {

  const [ email, setEmail ] = useState('');
  const [ password, setPassword ] = useState('');
  const [ twoFactorCode, setTwoFactorCode ] = useState('');
  const [ trustThisBrowser, setTrustThisBrowser ] = useState(false);
  const [ showTwoFactorModal, setShowTwoFactorModal ] = useState(false);
  const [ invalidCredentials, setInvalidCredentials ] = useState(false);
  const [ accountDisabled, setAccountDisabled ] = useState(false);
  const [ accountUnverified, setAccountUnverified ] = useState(false);
  const [ lastAttempt, setLastAttempt ] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);  
  const [loginError, setLoginError] = useState(false); 
  const { dispatch } = useAuthContext()
  const { login, error, isLoading } = useLogin();
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [fadeOut, setFadeOut] = useState(false); // Controls the fade-out effect

  useEffect(() => {
    console.log(error);
    if (error === "Invalid Login Credentials") {
      setInvalidCredentials(true);
    } else if (error === "Account is disabled") {
      setAccountDisabled(true);
    } else if (error === "Account Not Verified") {
      setAccountUnverified(true);
    } else if (error === "Last Attempt") {
      setLastAttempt(true)
    } else if (error === "Request failed with status code 429" || error === "Request failed with status code 403") {
      setLoginError(true);
    }
  }, [error]);

  // check if user is logged in already, redirect to home if so
  useEffect(() => {
    if (user) {
      setFadeOut(true); // Trigger the fade-out
      setTimeout(() => {
        setShouldRedirect(true); // Redirect after the fade-out
      }, 500); // 500ms matches the fade-out duration
    }
  }, [user, navigate]);

  useEffect(() => {
    if (shouldRedirect) {
      navigate('/home');
    }
  }, [shouldRedirect, navigate]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setInvalidCredentials(false);
    setAccountDisabled(false);
    setAccountUnverified(false);
    setLastAttempt(false);
    
    try {
      const response = await login(email, password, trustThisBrowser);
      
      if (response.requiresTwoFactorAuth) {
        setShowTwoFactorModal(true);
      } else if (response.data && response.data.token) {
        // Only store and dispatch if we have a complete auth response
        localStorage.setItem("user", JSON.stringify(response.data));
        dispatch({ type: "LOGIN", payload: response.data });
        
        if (window.pendo) {
          window.pendo.identify({
            visitor: {
              id: response.data.email.toLowerCase(),
              name: response.data.firstName + ' ' + response.data.lastName,
            },
            account: {
              id: response.data.email.toLowerCase(),
            }
          });
        }
        
        navigate('/home');
      }
    } catch (error) {
      console.error(error);
    }
  };
  
  const handleTwoFactorSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await login(email, password, trustThisBrowser, twoFactorCode);
      
      if (response.data && response.data.token) {
        localStorage.setItem("user", JSON.stringify(response.data));
        dispatch({ type: "LOGIN", payload: response.data });
        
        if (window.pendo) {
          window.pendo.identify({
            visitor: {
              id: response.data.email.toLowerCase(),
              name: response.data.firstName + ' ' + response.data.lastName,
            }
          });
        }
        
        setShowTwoFactorModal(false);
        navigate('/home');
      }
    } catch (error) {
      console.error('2FA submit error:', error);
    }
  };


  const LoginErrorModal = () => { 
  
    return(
      <Modal size='lg' centered show={loginError}>
        <Modal.Header>
          Too Many Failed Login Attempts.
          <br></br>
          Please Try Again Later.
        </Modal.Header>
      </Modal>
    )
  }


  const isLucidusTech = window.location.href.startsWith('https://app.lucidustech.com')  
  || window.location.href.startsWith('http://localhost:3000') 
  || window.location.href.startsWith('https://stage.lucidustech.com');

  const isCodeFour = window.location.href.startsWith('https://app.codefour.ai');


  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {loginError && <LoginErrorModal />}
        <Modal
          size='lg'
          centered
          show={showTwoFactorModal}
          onHide={() => setShowTwoFactorModal(false)}
          backdrop="static"
        >
          <Modal.Header>
            <Modal.Title>
              Two Factor Authentication Required
            </Modal.Title>
            <Modal.Body>
              You Will Receive an Email with Your Two-Factor Authentication Code
              <Form onSubmit={handleTwoFactorSubmit}>
                <Form.Group>
                  <Form.Label>Two Factor Code</Form.Label>
                  <Form.Control 
                    type="text" 
                    placeholder="Type or Paste Your Two Factor Code"
                    value={twoFactorCode}
                    onChange={(e) => setTwoFactorCode(e.target.value)}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Check 
                    label="Check This Box to Trust This Browser for 30 Days"
                    checked={trustThisBrowser}
                    onChange={(e) => setTrustThisBrowser(e.target.checked)}
                  />
                </Form.Group>
                <Button type='submit' variant='primary' style={{ width: '100%' }} className='mt-3' disabled={isLoading}>
                  Submit
                </Button>
              </Form>
            </Modal.Body>
          </Modal.Header>
        </Modal>
        { user &&
          <Container style={{display: 'grid', height: '100vh', alignItems: 'center', justifyContent: 'center'}} >
        <Image
          src={lucidusLogo}
          height="120"
          width="350px"
          style={{
            opacity: fadeOut ? 0 : 1,
            transition: 'opacity 1s ease-in-out', // Smooth fade-out effect
          }}
        />          </Container>
        }
        { !user && 
        <Container style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', }}>
          <Row>
            <Col>
              <Form
                onSubmit={handleLoginSubmit}
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}
              >
                
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                    {isCodeFour ? (
                       <Image src={TraffickStopLogo} height='auto' width='350px' />
                    ) : (
                  <Image src={lucidusLogo} height='120' width='350px' />
                    )}
                  <Row className='mt-3 mb-3'>
                    {isCodeFour ? (
                      <h1 style={{ display: 'flex', justifyContent: 'center', width: '100%', }} className='my-3'>
                        Login to CodeFour
                      </h1>
                      
                    ) : (
                    <h1 style={{ display: 'flex', justifyContent: 'center', width: '100%', }} className='my-3'>
                      Login to Lucidus LE
                    </h1>
                    )}
                  </Row>
                </div>
                <Form.Group as={Col} className='my-1 mb-3' sm={12} md={6} lg={3} style={{ width: '100%' }}>
                  <FloatingLabel
                    controlId="floatingInput"
                    label="Email address"
                    className="mb-3"
                  >
                  <Form.Control
                    type='email'
                    placeholder='Enter Email'
                    size='lg'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  </FloatingLabel>
                </Form.Group>
                <Form.Group as={Col} className='my-1 mb-3' sm={12} md={6} lg={3} style={{ width: '100%' }}>
                  <FloatingLabel
                    controlId="floatingInput"
                    label="Password"
                    className="mb-3"
                  >                  
                  <Form.Control
                    type='password'
                    placeholder='Enter Password'
                    size='lg'
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  </FloatingLabel>
                </Form.Group>
                <Button type='submit' variant='primary' style={{ width: '100%' }} className='mt-3' disabled={isLoading}>
                  {isLoading ? <Spinner size='sm'></Spinner> : 'Login'}
                </Button>
                <Button
                  variant="link"
                  style={{ marginTop: '10px' }}
                  onClick={() => setShowForgotPasswordModal(true)}
                >
                  Forgot Password?
                </Button>
              </Form>
              <ResetPassModal
                show={showForgotPasswordModal}
                onHide={() => setShowForgotPasswordModal(false)}
              />
              {invalidCredentials && (
                <Row className='my-3' style={{ display: 'flex', justifyContent: 'center' }}>
                  <Col>
                    <Alert variant='danger'>
                      <h6 style={{ textAlign: 'center' }}>Invalid Login Credentials</h6>
                    </Alert>
                  </Col>
                </Row>
              )}
              {accountUnverified && (
                <Row className='my-3' style={{ display: 'flex', justifyContent: 'center' }}>
                  <Col>
                    <Alert variant='danger'>
                      <h6 style={{ textAlign: 'center' }}>Your Account Is Not Verified, <br></br>Please Contact system@lucidustech.com</h6>
                    </Alert>
                  </Col>
                </Row>
              )}
              {accountDisabled && (
                <Row className='my-3' style={{ display: 'flex', justifyContent: 'center' }}>
                  <Col>
                    <Alert variant='danger'>
                      <h6 style={{ textAlign: 'center' }}>Your Account Has Been Disabled, <br></br>Please Contact system@lucidustech.com</h6>
                    </Alert>
                  </Col>
                </Row>
              )}
              {lastAttempt && (
                <Row className='my-3' style={{ display: 'flex', justifyContent: 'center' }}>
                  <Col>
                    <Alert variant='danger'>
                      <h6 style={{ textAlign: 'center' }}>You have 1 Attempt Remaining <br></br> Before Your Account is Disabled</h6>
                    </Alert>
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
        </Container>
        }
    </div>
  )
}

export default LoginScreen
